// eslint-disable-next-line no-restricted-imports
import { Transition } from "@headlessui/react";
import { format, isAfter, sub } from "date-fns";
import { groupBy } from "lodash-es";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { CloseOverlayButton } from "../sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "../sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "../sidebar/ContentSidebarMain";
import { OverlayContainer } from "../sidebar/OverlayContainer";

import { ChatCompose, ChatHistory, UserChatBubble } from "components/chat";
import {
  CHAT_MESSAGE_VISIBILITY_THRESHOLD,
  CLASS_CHAT_HISTORY_ID,
  RECENT_MESSAGE_HOURS,
} from "components/chat/constants";
import { SecondaryOverlay } from "components/constants";
import type { ChatMessageListFilterType } from "components/server-filters";
import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import {
  ChatWithLinesIcon,
  ChevronDownIcon,
  DotMenuVerticalIcon,
  EyeNotVisibleIcon,
} from "mds/icons";
import { useUserIdsWhoCanAuthorSection } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  filterChatMentionTargetsForAuthor,
  getChatMentionAuthorPermissions,
  selectChatMentionAuthorPermissionsByUserId,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectMentionTargetsForChatHistory,
  selectMentionTargetsForCurrentClass,
  selectRequestedChatMentionMessageIdForCurrentSection,
  selectSortedChatMessagesForCurrentSection,
  selectUnreadChatMessageCountForCurrentSection,
} from "store/selectors";
import { chatMentionsActions } from "store/slices/chat-mentions";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";

export const ClassChatSidebarPanel: FC = () => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const unreadChatMessageCount = useAppSelector(selectUnreadChatMessageCountForCurrentSection);
  const mentionTargets = useAppSelector(selectMentionTargetsForCurrentClass);
  const historyMentionTargets = useAppSelector(selectMentionTargetsForChatHistory);
  const mentionAuthorPermissionsByUserId = useAppSelector(
    selectChatMentionAuthorPermissionsByUserId,
  );
  const requestedMentionMessageId = useAppSelector(
    selectRequestedChatMentionMessageIdForCurrentSection,
  );
  const authoringUserIds = new Set(useUserIdsWhoCanAuthorSection());

  const [showPastMessages, setShowPastMessages] = useState(false);

  const dispatch = useAppDispatch();

  const updateClassChatMessagesReadAt = useCallback(
    (readAt: string) => {
      if (currentSectionId) {
        dispatch(
          localStateActions.updateClassChatMessagesReadAt({ sectionId: currentSectionId, readAt }),
        );
      }
    },
    [currentSectionId, dispatch],
  );

  const handleMessageVisible = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const intersectingEntries = entries.filter(
        (entry) => entry.intersectionRatio >= CHAT_MESSAGE_VISIBILITY_THRESHOLD,
      );
      if (intersectingEntries.length === 0) return;

      const visibleMessageIds = intersectingEntries.flatMap((entry) => {
        const messageId = (entry.target as HTMLDivElement).dataset.chatMessageId;
        return messageId ? [messageId] : [];
      });
      if (currentSectionId && visibleMessageIds.length > 0) {
        dispatch(
          chatMentionsActions.markMentionsSeen({
            messageIds: visibleMessageIds,
            sectionId: currentSectionId,
          }),
        );
      }

      const mostRecentSentAt = intersectingEntries.reduce<string | null>((sentAt, entry) => {
        const node = entry.target as HTMLDivElement;
        // Look up the time this message was sent, by finding the `time` element
        // inside the message node and getting its `dateTime` attribute.
        const entrySentAt = node.querySelector("time")?.getAttribute("dateTime");
        if (!entrySentAt) return sentAt;

        // If this is the first entry, return it
        if (!sentAt) return entrySentAt;

        // Compare the sentAt of the current entry with the most recent one found so far
        return isAfter(entrySentAt, sentAt) ? entrySentAt : sentAt;
      }, null);

      if (!mostRecentSentAt) return;

      // Update the readAt timestamp in the local state
      updateClassChatMessagesReadAt(mostRecentSentAt);
    },
    [currentSectionId, dispatch, updateClassChatMessagesReadAt],
  );

  useEffect(() => {
    if (currentSectionId) {
      const listParams: Partial<ChatMessageListFilterType> = {
        section_id: currentSectionId,
      };
      if (!showPastMessages) {
        listParams.created_at__within = `${RECENT_MESSAGE_HOURS}h`;
      }
      storeApi.chat_messages.list(listParams);
    }
  }, [currentSectionId, showPastMessages]);

  const recentMessageCutoff = sub(new Date(), { hours: RECENT_MESSAGE_HOURS });

  const chatMessages = useListSelector(selectSortedChatMessagesForCurrentSection);
  const visibleChatMessages = showPastMessages
    ? chatMessages
    : chatMessages.filter((message) => isAfter(message.created_at, recentMessageCutoff));
  const requestedMentionIsVisible = visibleChatMessages.some(
    (message) => message.id === requestedMentionMessageId,
  );
  const chatMessagesByDate = groupBy(visibleChatMessages, (message) =>
    format(new Date(message.created_at), "yyyy-MM-dd"),
  );

  const historyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!currentSectionId || !requestedMentionMessageId || !requestedMentionIsVisible) return;

    const messageNode = Array.from(
      historyRef.current?.querySelectorAll<HTMLElement>("[data-chat-message-id]") ?? [],
    ).find((node) => node.dataset.chatMessageId === requestedMentionMessageId);
    if (!messageNode) return;

    messageNode.scrollIntoView({ behavior: "smooth", block: "center" });
    dispatch(chatMentionsActions.clearMentionScrollRequest({ sectionId: currentSectionId }));
  }, [currentSectionId, dispatch, requestedMentionIsVisible, requestedMentionMessageId]);

  function scrollHistoryToTop() {
    historyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (!currentSectionId || !currentUserId) {
    return null;
  }

  return (
    <OverlayContainer className="bg-white">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <ChatWithLinesIcon className="text-blue" />
          {getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.ClassChat)}
        </div>

        <div className="flex items-center gap-2">
          <Menu
            menuButton={
              <SquareButton kind="tertiary" size="s" title={t("common.options")}>
                <DotMenuVerticalIcon />
              </SquareButton>
            }
          >
            {showPastMessages ? (
              <MenuItem onClick={() => setShowPastMessages(false)}>
                <IconText
                  iconStart={<EyeNotVisibleIcon className="text-black-tint-55" />}
                  text={t("chat.hide_past_messages")}
                />
              </MenuItem>
            ) : (
              <MenuItem onClick={() => setShowPastMessages(true)}>
                <IconText iconStart={<ChatWithLinesIcon />} text={t("chat.show_past_messages")} />
              </MenuItem>
            )}
          </Menu>
          <CloseOverlayButton focusPanelTrigger={SecondaryOverlay.ClassChat} />
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <div className="flex h-full flex-col">
          <div className="relative grow">
            <ChatHistory
              className="absolute inset-0 overflow-y-auto overflow-x-hidden"
              id={CLASS_CHAT_HISTORY_ID}
              messagesByDate={Object.entries(chatMessagesByDate)}
              ref={historyRef}
              renderMessage={(message, ref) => (
                <UserChatBubble
                  key={message.id}
                  mentionTargets={filterChatMentionTargetsForAuthor(
                    historyMentionTargets,
                    getChatMentionAuthorPermissions(
                      mentionAuthorPermissionsByUserId,
                      message.author_id,
                    ),
                  )}
                  message={message}
                  ref={ref}
                  showStar={authoringUserIds.has(message.author_id)}
                  parseMarkdown
                />
              )}
              visibilityThreshold={CHAT_MESSAGE_VISIBILITY_THRESHOLD}
              onMessageVisible={handleMessageVisible}
            />
            <Transition show={unreadChatMessageCount > 0}>
              <button
                aria-label={t("chat.scroll_to_recent_messages")}
                className="group absolute bottom-0 flex h-10 w-full items-center justify-center bg-gradient-to-t from-white/100 via-white/80 to-white/0 transition duration-500 data-[closed]:opacity-0"
                type="button"
                onClick={scrollHistoryToTop}
              >
                <ChevronDownIcon className="text-black-tint-20 group-hover:animate-bounce" />
              </button>
            </Transition>
          </div>
          <ChatCompose
            className="mt-2"
            context="class"
            mentionTargets={mentionTargets}
            placeholder={t("chat.send_a_message_all")}
            onSend={(text) =>
              storeApi.chat_messages.create({
                author_id: currentUserId,
                section_id: currentSectionId,
                text,
              })
            }
          />
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
